import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, maxBy, PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_Y_AXIS,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { EMPTY, formatNumber, plural } from '../../utils/format';

/** "Meeting-room panic is real" holds only when a good share of rooms are booked at the last minute. */
const PANIC_SHARE = 25;
/** "Just N days ahead" holds only for a short average lead time. */
const SHORT_LEAD_DAYS = 2;

/** "3.2 days", or hours under a day: the backend rounds the average to a tenth of a day. */
const leadTime = (leadDays) => {
  if (leadDays >= 1) return plural(leadDays, 'day');
  if (leadDays > 0) return `about ${plural(Math.round(leadDays * 24), 'hour')}`;
  return 'under 2 hours';
};
/** "just 1.4 days", "only about 5 hours": said only when the lead time is short. */
const leadPrefix = (leadDays) => {
  if (leadDays >= SHORT_LEAD_DAYS || leadDays === 0) return '';
  return leadDays >= 1 ? 'just ' : 'only ';
};

/**
 * The backend has no lead-time buckets, so this page charts the hour of day bookings are made,
 * alongside the share booked within an hour of starting and the average lead time.
 */
const MeetingLeadTime = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(90);
  const { data, loading, error, reload } = useInsightData(() => insightsService.meetingLeadTime(days), [days]);
  const d = data || {};
  const byHour = d.byHour || [];
  const made = byHour.reduce((s, r) => s + r.bookings, 0);
  const peak = made > 0 ? maxBy(byHour, 'bookings') : null;

  const copy = d.total > 0
    ? [
      `${d.pctLastMinute >= PANIC_SHARE ? 'Meeting-room panic is real: ' : ''}${d.pctLastMinute}% of meeting rooms are booked within an hour of the meeting starting.`,
      `The average room is booked ${leadPrefix(d.avgLeadDays)}${leadTime(d.avgLeadDays)} ahead.`,
      peak && `The busiest time to book is ${peak.hour}.`,
      '⏰',
    ].filter(Boolean).join(' ')
    : '';

  return (
    <InsightPage
      title="When meeting rooms get booked"
      description="The hour of day people book meeting rooms, and how many bookings are made at the last minute."
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={made === 0}
      emptyMessage="No meeting room bookings made in this window"
    >
      <StatRow>
        <StatCard xs={4} label="Busiest booking hour" value={peak?.hour || EMPTY} sub={peak ? plural(peak.bookings, 'booking') : ''} variant="primary" />
        <StatCard
          xs={4}
          label="Booked within an hour of starting"
          value={`${d.pctLastMinute}%`}
          sub={`${formatNumber(d.bookedWithinHour)} of ${plural(d.total, 'booking')}`}
        />
        <StatCard xs={4} label="Average lead time" value={leadTime(d.avgLeadDays)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">When bookings are made (by hour of day)</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 400}>
            <BarChart data={byHour} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              {/* Phones label every fourth hour, level, instead of cramming in every other one at an angle */}
              <XAxis
                dataKey="hour"
                {...t.xAxis}
                interval={1}
                angle={-45}
                textAnchor="end"
                height={60}
                {...(isMobile ? { interval: 3, angle: 0, textAnchor: 'middle', height: 30 } : {})}
              />
              <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="bookings" name="Bookings made">
                {byHour.map((r) => (
                  <Cell key={r.hour} fill={r === peak ? t.colors.emphasis : t.colors.neutral} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default MeetingLeadTime;
